import type { Theme, SxProps, Breakpoint } from '@mui/material/styles';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { CONFIG } from 'src/config-global';

import { Logo } from 'src/components/logo';

import { alpha, useTheme, useColorScheme } from '@mui/material/styles';
import { allLangs } from 'src/locales';
import { LanguagePopover } from 'src/layouts/components/language-popover';
import { useSettingsContext } from 'src/components/settings';
import { IconButton, Stack, Typography, useMediaQuery, Drawer, Button } from '@mui/material';
import { red } from '@mui/material/colors';
import { Iconify } from 'src/components/iconify';
import { Icon } from '@iconify/react';
import ParkCityLogo from 'src/components/logo/logo_new';
import { Section } from './section';
import { Main, Content } from './main';
import { HeaderSection } from '../core/header-section';
import { LayoutSection } from '../core/layout-section';
import { SettingsButton } from '../components/settings-button';
import { useAuthContext } from 'src/context/AuthContext';
import { useEffect, useState } from 'react';
import { bgGradient, varAlpha } from 'src/theme/styles';
import FormConstants from 'src/constants/FormConstants';
import EncryptUtils from 'src/utils/EncryptUtils';
import { getErrorMessage } from 'src/utils/ErrorUtils';
import { toast } from 'src/components/snackbar';
import { signOutUser } from 'src/services/service-handlers/PrivateService';
import AuthUtils from 'src/utils/AuthUtils';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useLocation, useNavigate } from 'react-router';
// ----------------------------------------------------------------------

export type AuthSplitLayoutProps = {
  sx?: SxProps<Theme>;
  children: React.ReactNode;
  header?: {
    sx?: SxProps<Theme>;
    slots?: {
      leftArea?: React.ReactNode;
      rightArea?: React.ReactNode;
      topArea?: React.ReactNode;
      centerArea?: React.ReactNode;
      bottomArea?: React.ReactNode;
      drawerHeader?: React.ReactNode;
    };
  };
  section?: {
    title?: string;
    imgUrl?: string | boolean;
    subtitle?: string;
    bgImage?: string | {
      dark?: string;
      light?: string;
    };
    customSection?: any;
    renderBackButton?: boolean;
    backButtonPage?: string;
    showLogout?: boolean;
  };
};

export function AuthSplitLayout({ sx, section, children, header }: AuthSplitLayoutProps) {
  const layoutQuery: Breakpoint = 'md';

  const theme = useTheme();

  const settings = useSettingsContext();

  const { mode, setMode } = useColorScheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [openDrawer, setOpenDrawer] = useState(false);

  const isAzureAuthenticated = useIsAuthenticated();

  const location = useLocation();

  const navigate = useNavigate();

  const { instance } = useMsal();

  const { logout } = useAuthContext();

  const { authState } = useAuthContext();
  const { isAdmin, isAuthenticated } = authState;

  // Close the drawer on route change
  useEffect(() => {
    if (location) {
      setOpenDrawer(false);
    }
  }, [location]);

  // Close drawer when switching from mobile to desktop
  useEffect(() => {
    if (!isMobile && openDrawer) {
      setOpenDrawer(false);
    }
  }, [isMobile, openDrawer]);

  // Close the drawer when the back button is pressed (e.g., browser or mobile)
  useEffect(() => {
    const handlePopState = () => {
      setOpenDrawer(false);
    };

    window.addEventListener('popstate', handlePopState);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const handleLogOut = async () => {
    try {
      const data: any = {
        sql_data: {
          is_audit: true,
          audit_event_description: FormConstants.COMMON.LOGOUT_SUCCESS,
        },
      };
      const encryptRequestData = EncryptUtils.encryptedData(data);
      await signOutUser({
        tk: encryptRequestData,
      });
      if (isAzureAuthenticated || AuthUtils.getIsAzureAuthenticated()) {
        instance.logoutRedirect({
          postLogoutRedirectUri: '/',
        });
      } else {
        logout();
      }
      AuthUtils.clearStorage();

      // Redirect based on admin status
      navigate(isAdmin ? '/admin/sign-in' : '/sign-in', { replace: true });
    } catch (e) {
      const errMessage = getErrorMessage(e);
      toast.error(errMessage);
    }
  };

  return (
    <LayoutSection
      headerSection={
        /** **************************************
         * Header
         *************************************** */
        <HeaderSection
          disableElevation
          layoutQuery={layoutQuery}
          slotProps={{ container: { maxWidth: false } }}
          setOpenDrawer={setOpenDrawer}
          sx={{
            position: { [layoutQuery]: 'fixed' },
            ...header?.sx,
            // [theme.breakpoints.down(layoutQuery)]: { width: '75vw' },
          }}
          slots={{
            topArea: header?.slots?.topArea ?? (
              <Alert severity="info" sx={{ display: 'none', borderRadius: 0 }}>
                This is an info Alert.
              </Alert>
            ),
            leftArea: header?.slots?.leftArea ?? (
              <>
                {/* -- Logo -- */}
                {isMobile && (
                  <Box
                    component={RouterLink as React.ElementType}
                    to="/"
                    sx={{
                      height: '3rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      textDecoration: 'none',
                    }}
                  >
                    <ParkCityLogo />
                  </Box>
                )}
                {/* Mobile View */}
                {/* {isMobile && (
                  <Stack direction="row" justifyContent="flex-end" width="100%">
                    <IconButton onClick={() => setOpenDrawer(true)}>
                      <Iconify
                        icon="mdi:hamburger-menu"
                        height={124}
                        color={mode === 'dark' ? red[400] : undefined}
                      />
                    </IconButton>
                  </Stack>
                )} */}
              </>
            ),
            centerArea: header?.slots?.centerArea ?? <></>,
            rightArea:
              header?.slots?.rightArea ??
              (!isMobile ? (
                <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 1.5 }}>
                  {/* -- Help link -- */}
                  <Link
                    target="_blank"
                    href={paths.faqs}
                    component={RouterLink}
                    color="inherit"
                    sx={{ typography: 'subtitle2' }}
                  >
                    Need help?
                  </Link>
                  {/* -- Settings button -- */}
                  <LanguagePopover data={allLangs} />
                  <IconButton
                    onClick={() => {
                      settings.onUpdateField('colorScheme', mode === 'light' ? 'dark' : 'light');
                      setMode(mode === 'light' ? 'dark' : 'light');
                    }}
                  >
                    <Iconify
                      icon={
                        mode === 'dark'
                          ? 'line-md:moon-filled-to-sunny-filled-loop-transition'
                          : 'line-md:moon-rising-filled-loop'
                      }
                      height={48}
                      color={mode === 'dark' ? red[400] : '-moz-initial'}
                    />
                  </IconButton>
                </Box>
              ) : (
                isMobile && (
                  <Stack direction="row" justifyContent="flex-end" width="100%">
                    <IconButton onClick={() => setOpenDrawer(true)}>
                      {/* <MenuIcon style={{ fontSize: 32, color: red[400] }} /> */}
                      <Iconify
                        icon="mdi:hamburger-menu"
                        height={124}
                        color={mode === 'dark' ? red[400] : undefined}
                      />
                    </IconButton>
                  </Stack>
                )
              )),
          }}
        />
      }
      /** **************************************
       * Footer
       *************************************** */
      footerSection={null}
      /** **************************************
       * Style
       *************************************** */
      cssVars={{ '--layout-auth-content-width': '420px' }}
      sx={sx}
    >
      <Main layoutQuery={layoutQuery}>
        <Section
          title={section?.title}
          layoutQuery={layoutQuery}
          imgUrl={section?.imgUrl}
          subtitle={section?.subtitle}
          bgImage={typeof section?.bgImage === 'string' ? section.bgImage : (theme.palette.mode === 'dark' ? section?.bgImage?.dark : section?.bgImage?.light)}
          customSection={section?.customSection}
          renderBackButton={section?.renderBackButton}
          backButtonPage={section?.backButtonPage}
          showLogout={section?.showLogout}
        />
        <Content layoutQuery={layoutQuery}>{children}</Content>
        <Drawer
          anchor="left"
          open={openDrawer}
          onClose={() => setOpenDrawer(false)}
          variant="temporary"
          sx={{
            '& .MuiDrawer-paper': {
              ...(typeof section?.bgImage === 'string' && section.bgImage
                ? {
                    position: 'relative',
                    overflow: 'hidden',
                    width: 260,
                    maxWidth: '100%',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundImage: `url(${section.bgImage})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'left top',
                      backgroundRepeat: 'no-repeat',
                      filter: theme.palette.mode === 'dark' ? 'brightness(0.3)' : 'none',
                      zIndex: 0,
                      pointerEvents: 'none',
                    },
                    // Ensure content is above the dimmed background
                    '& > *': {
                      position: 'relative',
                      zIndex: 1,
                    },
                  }
                : bgGradient({
                    color: `0deg, ${varAlpha(
                      theme.vars.palette.background.defaultChannel,
                      0.92
                    )}, ${varAlpha(theme.vars.palette.background.defaultChannel, 0.92)}`,
                    imgUrl: `${CONFIG.assetsDir}/assets/background/background-3-blur.webp`,
                  })),
            },
          }}
        >
          <Box
            width={260}
            height={1}
            p={2}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                padding: 1,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box
                component={RouterLink as React.ElementType}
                to="/"
                sx={{
                  height: '3rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                }}
              >
                <ParkCityLogo />
              </Box>

              <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center">
                {/* -- Settings button -- */}

                {header?.slots?.drawerHeader || (
                  <>
                    {' '}
                    <LanguagePopover data={allLangs} />
                    <IconButton
                      onClick={() => {
                        settings.onUpdateField('colorScheme', mode === 'light' ? 'dark' : 'light');
                        setMode(mode === 'light' ? 'dark' : 'light');
                      }}
                    >
                      <Iconify
                        icon={
                          mode === 'dark'
                            ? 'line-md:moon-filled-to-sunny-filled-loop-transition'
                            : 'line-md:moon-rising-filled-loop'
                        }
                        height={48}
                        color={mode === 'dark' ? red[400] : '-moz-initial'}
                      />
                    </IconButton>
                  </>
                )}
              </Box>
            </Box>
            {section?.customSection}
          </Box>
          {
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              justifySelf="flex-end"
              gap={{ xs: 1, sm: 1.5 }}
              sx={{ position: 'absolute', bottom: 20, width: '100%' }}
            >
              {/* -- Help link -- */}
              <Link
                target="_blank"
                href={paths.faqs}
                component={RouterLink}
                color="white"
                sx={{ typography: 'subtitle2' }}
                gap={{ xs: 1, sm: 1.5 }}
              >
                Need help?
              </Link>
              {section?.showLogout && (
                <Button
                  startIcon={<Icon icon="tabler:logout-2" color="black" />}
                  variant="contained"
                  sx={{
                    width: '60%',
                    backgroundColor: '#f0eff4',
                    color: 'black',
                    mx: 5,
                    display: 'flex',
                    justifyContent: 'center',
                    '&:hover': {
                      backgroundColor: '#f8eeec', // Change background color on hover
                      color: '#000000', // Change text color on hover
                    },
                  }}
                  onClick={handleLogOut}
                >
                  Logout
                </Button>
              )}
            </Box>
          }
        </Drawer>
      </Main>
    </LayoutSection>
  );
}
